import type { PostingsV3DataQuery, PostingsV3Ent } from '@revelio/data-access';

export type MarketMetricPoint = { dim: string; value: number };

export type PostingsByRcid = Map<number, number>;

type PostingsV3Entity = NonNullable<
  PostingsV3DataQuery['postings_v3']
>['entities'][number];

const getLatestActivePostingCount = (
  entity: PostingsV3Entity
): number | null => {
  const latest = entity.metrics.active.reduce<
    { id: number; val: number } | undefined
  >((currentLatest, point) => {
    if (!currentLatest || point.id > currentLatest.id) return point;
    return currentLatest;
  }, undefined);

  return typeof latest?.val === 'number' ? Math.round(latest.val) : null;
};

const getRequestRcid = (
  entity: PostingsV3Entity,
  requestEntities: ReadonlyArray<PostingsV3Ent>
): number | null => {
  const requestEntity = requestEntities[entity.id];
  return typeof requestEntity?.EntID === 'number' ? requestEntity.EntID : null;
};

export const extractActivePostingsFromPostingsV3 = ({
  data,
  requestEntities,
  entityLabelByRcid,
}: {
  data: PostingsV3DataQuery | undefined;
  requestEntities: ReadonlyArray<PostingsV3Ent>;
  entityLabelByRcid: ReadonlyMap<number, string>;
}): MarketMetricPoint[] => {
  const entities = data?.postings_v3?.entities ?? [];

  return entities.flatMap((entity) => {
    const rcid = getRequestRcid(entity, requestEntities);
    if (rcid === null) return [];

    const label = entityLabelByRcid.get(rcid);
    if (!label) return [];

    const count = getLatestActivePostingCount(entity);
    return count !== null ? [{ dim: label, value: count }] : [];
  });
};

export const buildPostingsByRcidFromPostingsV3 = ({
  data,
  requestEntities,
}: {
  data: PostingsV3DataQuery | undefined;
  requestEntities: ReadonlyArray<PostingsV3Ent>;
}): PostingsByRcid => {
  const map: PostingsByRcid = new Map();
  const entities = data?.postings_v3?.entities ?? [];

  for (const entity of entities) {
    const rcid = getRequestRcid(entity, requestEntities);
    if (rcid === null) continue;

    const count = getLatestActivePostingCount(entity);
    if (count !== null) {
      map.set(rcid, count);
    }
  }

  return map;
};
